import React, {useMemo, useState} from 'react'
import {
  PROPERTY_TYPES_DESKTOP_LIMIT,
  PROPERTY_TYPES_MOBILE_LIMIT
} from 'components/Filters/constants'
import {getIsFilterSelected, getSortedFilters} from 'components/Filters/utils'
import {FilterOptionItem} from 'modules/search/types'

import {Box} from '@vio/atlas/helpers/Box'
import {Link} from '@vio/atlas/Link'
import {Checkbox} from '@vio/atlas/src/components/designSystem/Checkbox'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {PropertyTypesProps} from './PropertyTypes'

interface Props
  extends Pick<
    PropertyTypesProps,
    'filters' | 'appliedFilters' | 'toggleMultiFilter' | 'isPopover'
  > {
  propertyTypes: FilterOptionItem[]
}
export const PropertyTypesList: React.FC<Props> = ({
  propertyTypes,
  filters,
  appliedFilters,
  toggleMultiFilter,
  isPopover
}) => {
  const {isDesktop} = useDeviceLayout()

  const [isExpanded, setIsExpanded] = useState(false)
  const currentLimit = useMemo(() => {
    return isDesktop
      ? PROPERTY_TYPES_DESKTOP_LIMIT
      : PROPERTY_TYPES_MOBILE_LIMIT
  }, [isDesktop])

  const {mainFacets, extraFacetsCount, canExpand} = useMemo(() => {
    const availableFacets = getSortedFilters(
      propertyTypes,
      appliedFilters
    ).filter(facet => facet.isAvailable)
    const canExpand = availableFacets.length > currentLimit
    const mainFacets =
      canExpand && !isExpanded
        ? availableFacets.slice(0, currentLimit)
        : availableFacets

    return {
      mainFacets,
      extraFacetsCount: availableFacets.length - mainFacets.length,
      canExpand
    }
  }, [propertyTypes, isExpanded, appliedFilters, currentLimit])

  return (
    <>
      <Box
        display="grid"
        gap="s400"
        gridTemplateColumns="repeat(1, 1fr)"
        desktopXs={{
          gridTemplateColumns: isPopover ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)'
        }}
        paddingBottom="s500"
      >
        {mainFacets.map(facet => (
          <Box key={facet.id}>
            <Checkbox
              label={facet.label ?? ''}
              name={facet.label ?? ''}
              checked={getIsFilterSelected(filters, facet.id)}
              disabled={
                !facet.isAvailable &&
                !getIsFilterSelected(appliedFilters, facet.id)
              }
              onChange={() => toggleMultiFilter('propertyTypes', facet.id)}
            />
          </Box>
        ))}
      </Box>
      {canExpand && (
        <div onClick={() => setIsExpanded(prev => !prev)} role="presentation">
          <Link size="sm">
            {isExpanded ? (
              <FormattedMessageWrapper defaultMessage="Less" id="less" />
            ) : (
              <FormattedMessageWrapper
                values={{
                  number: extraFacetsCount
                }}
                defaultMessage="+{number} more"
                id="moreWithNumber"
              />
            )}
          </Link>
        </div>
      )}
    </>
  )
}
